import { useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { type SortingState } from "@tanstack/react-table";
import { useDebounce } from "@/hooks/use-debounce";
import { Pagination } from "@/components/pagination";
import { AuditLogFilters } from "./audit-log-filters";
import { AdminTab } from "./admin-tab";
import { SecurityTab } from "./security-tab";
import { LoginTab } from "./login-tab";

export const Route = createFileRoute("/_admin/audit-logs/")({
  component: AuditLogsPage,
});

type TabId = "admin" | "security" | "login-attempts";

const PAGE_SIZE = 20;

const TABS: { id: TabId; label: string }[] = [
  { id: "admin", label: "Admin Logs" },
  { id: "security", label: "Security Logs" },
  { id: "login-attempts", label: "Login Attempts" },
];

function computeSince(preset: string): string | undefined {
  if (preset === "all") return undefined;
  const now = Date.now();
  const ms: Record<string, number> = {
    "1h": 60 * 60 * 1000,
    "24h": 24 * 60 * 60 * 1000,
    "7d": 7 * 24 * 60 * 60 * 1000,
    "30d": 30 * 24 * 60 * 60 * 1000,
  };
  const delta = ms[preset];
  if (!delta) return undefined;
  return new Date(now - delta).toISOString();
}

function AuditLogsPage() {
  // Tab + filter state
  const [activeTab, setActiveTab] = useState<TabId>("admin");
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [actorTypeFilter, setActorTypeFilter] = useState("");
  const [ipFilter, setIpFilter] = useState("");
  const [successFilter, setSuccessFilter] = useState<string>("all");
  const [sincePreset, setSincePreset] = useState("all");
  const [since, setSince] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);
  const [total, setTotal] = useState(0);

  const debouncedSearch = useDebounce(search, 300);
  const debouncedIp = useDebounce(ipFilter, 300);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Tab change resets all filters
  const handleTabChange = useCallback((tab: TabId) => {
    setActiveTab(tab);
    setPage(1);
    setSearch("");
    setActionFilter("");
    setActorTypeFilter("");
    setIpFilter("");
    setSuccessFilter("all");
    setSincePreset("all");
    setSince(undefined);
    setTotal(0);
    setSorting([{ id: tab === "login-attempts" ? "attempted_at" : "created_at", desc: true }]);
  }, []);

  const resetPage = useCallback(() => {
    setPage(1);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      resetPage();
    },
    [resetPage],
  );

  const handleActionFilterChange = useCallback(
    (value: string) => {
      setActionFilter(value);
      resetPage();
    },
    [resetPage],
  );

  const handleActorTypeFilterChange = useCallback(
    (value: string) => {
      setActorTypeFilter(value);
      resetPage();
    },
    [resetPage],
  );

  const handleIpFilterChange = useCallback(
    (value: string) => {
      setIpFilter(value);
      resetPage();
    },
    [resetPage],
  );

  const handleSuccessFilterChange = useCallback(
    (value: string) => {
      setSuccessFilter(value);
      resetPage();
    },
    [resetPage],
  );

  const handleSincePresetChange = useCallback(
    (value: string) => {
      setSincePreset(value);
      setSince(computeSince(value));
      resetPage();
    },
    [resetPage],
  );

  const handleTotalChange = useCallback((newTotal: number) => {
    setTotal(newTotal);
  }, []);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">Audit Logs</h1>

      {/* Tab buttons */}
      <div className="inline-flex gap-1 rounded-lg bg-slate-100 p-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab.id)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <AuditLogFilters
        activeTab={activeTab}
        search={search}
        onSearchChange={handleSearchChange}
        actionFilter={actionFilter}
        onActionFilterChange={handleActionFilterChange}
        actorTypeFilter={actorTypeFilter}
        onActorTypeFilterChange={handleActorTypeFilterChange}
        ipFilter={ipFilter}
        onIpFilterChange={handleIpFilterChange}
        successFilter={successFilter}
        onSuccessFilterChange={handleSuccessFilterChange}
        sincePreset={sincePreset}
        onSincePresetChange={handleSincePresetChange}
      />

      {/* Tab content */}
      {activeTab === "admin" && (
        <AdminTab
          search={debouncedSearch}
          actionFilter={actionFilter}
          actorTypeFilter={actorTypeFilter}
          since={since}
          page={page}
          sorting={sorting}
          onSortingChange={setSorting}
          onTotalChange={handleTotalChange}
        />
      )}

      {activeTab === "security" && (
        <SecurityTab
          search={debouncedSearch}
          actionFilter={actionFilter}
          ipFilter={debouncedIp}
          since={since}
          page={page}
          sorting={sorting}
          onSortingChange={setSorting}
          onTotalChange={handleTotalChange}
        />
      )}

      {activeTab === "login-attempts" && (
        <LoginTab
          search={debouncedSearch}
          ipFilter={debouncedIp}
          successFilter={successFilter}
          since={since}
          page={page}
          sorting={sorting}
          onSortingChange={setSorting}
          onTotalChange={handleTotalChange}
        />
      )}

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
